
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { NameFields } from "../NameFields";
import { Card } from "@/components/ui/card";
import CountryFlag from "@/components/country/CountryFlag";
import { supabase } from "@/integrations/supabase/client";
import { DatePickerField } from "../DatePickerField";
import { subYears } from "date-fns";

const personalInfoSchema = z.object({
  first_name: z.string().min(2, "First name must be at least 2 characters"),
  middle_name: z.string().optional(),
  last_name: z.string().min(2, "Last name must be at least 2 characters"),
  date_of_birth: z.date({
    required_error: "Date of birth is required",
    invalid_type_error: "Date of birth is required",
  }).refine((date) => {
    // Must be at least 18 years old
    const minAgeDate = subYears(new Date(), 18);
    return date <= minAgeDate;
  }, "You must be at least 18 years old"),
});

type PersonalInfoFormValues = z.infer<typeof personalInfoSchema>;

interface VerificationStep2Props {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}

const VerificationStep2 = ({ formData, setFormData }: VerificationStep2Props) => {
  const form = useForm<PersonalInfoFormValues>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      first_name: formData.personalInfo.first_name,
      middle_name: formData.personalInfo.middle_name,
      last_name: formData.personalInfo.last_name,
      date_of_birth: formData.personalInfo.date_of_birth ? new Date(formData.personalInfo.date_of_birth) : undefined,
    },
  });
  
  const [countryName, setCountryName] = useState<string>("");
  
  // Update parent formData when this form changes
  useEffect(() => {
    const subscription = form.watch((value) => {
      setFormData((prev: any) => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          first_name: value.first_name,
          middle_name: value.middle_name,
          last_name: value.last_name,
          date_of_birth: value.date_of_birth,
        },
      }));
    });
    
    return () => subscription.unsubscribe();
  }, [form, setFormData]);
  
  // Load country name on mount
  useEffect(() => {
    if (!formData.verificationCountry) return;
    
    const getCountryName = async () => {
      const { data } = await supabase
        .from('countries')
        .select('name')
        .eq('code', formData.verificationCountry)
        .single();
        
      if (data) {
        setCountryName(data.name);
      }
    };
    
    getCountryName();
  }, [formData.verificationCountry]);

  // Get the residency status label
  const getResidencyStatusLabel = (status: string) => {
    switch (status) {
      case 'resident': return 'Resident';
      case 'expatriate': return 'Expatriate';
      case 'visitor': return 'Visitor';
      case 'dual_citizen': return 'Dual Citizen';
      case 'other': return 'Other';
      default: return status;
    }
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold mb-2">Personal Information</h2>
        <p className="text-muted-foreground mb-6">
          Please provide your personal details as they appear on your ID documents.
        </p>
      </div>
      
      {/* Display verification details (read-only) */}
      <Card className="p-4 bg-slate-50 dark:bg-gray-800">
        <div className="flex flex-col space-y-4">
          <div>
            <h3 className="text-sm font-medium mb-1">Verification Country</h3>
            <div className="flex items-center gap-2 text-sm">
              {formData.verificationCountry && (
                <CountryFlag countryCode={formData.verificationCountry} className="h-4 w-6" />
              )}
              <span>{countryName || formData.verificationCountry}</span>
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium mb-1">Residency Status</h3>
            <div className="text-sm">
              {getResidencyStatusLabel(formData.personalInfo.residency_status)}
            </div>
          </div>
        </div>
      </Card>

      <Form {...form}>
        <div className="space-y-6">
          <NameFields form={form} />
          
          <DatePickerField 
            form={form} 
            name="date_of_birth"
            label="Date of Birth"
            description="You must be at least 18 years old to continue with verification."
          />
        </div>
      </Form>
    </div>
  );
};

export default VerificationStep2;
